import { useTranslation } from 'react-i18next'
import { ChevronLeft } from '@digital-twins/design-system/icons'
import { KnowledgeAskCitation } from '@digital-twins/shared-types'
import { cn } from '@/lib/utils'
import { ThreadSourceView } from './ThreadSourceView'

/**
 * What the sources drawer shows once a citation has been followed: the cited
 * pages themselves, one tab per document.
 *
 * This replaces the modal sheet the citations used to open. Checking a claim
 * means moving between the sentence and the page repeatedly, and a modal makes
 * that a sequence of open/close round-trips that hides the very text being
 * checked. Tabs are keyed by document rather than by citation so following
 * three citations into the same file reuses one tab, and every open tab stays
 * mounted so switching back does not refetch the page image.
 *
 * Content only: the drawer around it is ThreadSourcesDrawer, shared with the
 * sources list, so opening a source stays inside the same pane.
 */

export interface ThreadSourceTab {
  /** Document identity — citations for the same document share a tab. */
  key: string
  label: string
  citationIndex: number
  citation: KnowledgeAskCitation
}

interface ThreadSourcePanelProps {
  tabs: Array<ThreadSourceTab>
  activeKey: string | null
  /** Back to the sources list. The drawer itself stays open. */
  onCloseAll: () => void
  onRelabel: (key: string, label: string) => void
  onReportWrongPage?: (citation: KnowledgeAskCitation) => void
}

export function ThreadSourcePanel({
  tabs,
  activeKey,
  onCloseAll,
  onRelabel,
  onReportWrongPage,
}: ThreadSourcePanelProps) {
  const { t } = useTranslation('app')

  const active = tabs.find((tab) => tab.key === activeKey) ?? tabs[0] ?? null
  if (!active) return null

  return (
    <div
      className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
      data-testid="thread-source-panel"
    >
      {/* One way back, not a close: the drawer stays open and returns to the
          sources list, so the reader never loses the pane they came from. The
          document names itself right below, so a tab chip repeating it was the
          same string twice on two lines. */}
      <div className="flex items-center gap-1 px-2 py-1.5">
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs text-muted-foreground hover:bg-muted/60 hover:text-foreground"
          data-testid="thread-source-panel-close"
          onClick={onCloseAll}
        >
          <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
          {t('threads.detail.allSources')}
        </button>
      </div>

      {/* Every source followed in this thread stays mounted, hidden rather than
          unmounted: coming back to one keeps its fetched page preview and its
          scroll position instead of refetching. */}
      {tabs.map((tab) => (
        <div
          key={tab.key}
          hidden={tab.key !== active.key}
          className={cn(
            'min-h-0 flex-1 flex-col',
            tab.key === active.key ? 'flex' : 'hidden',
          )}
        >
          <ThreadSourceView
            citationIndex={tab.citationIndex}
            citation={tab.citation}
            documentLabel={tab.label}
            onReportWrongPage={onReportWrongPage}
            onResolveTitle={(title) => onRelabel(tab.key, title)}
          />
        </div>
      ))}
    </div>
  )
}
